import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppDispatch, useAppSelector } from '_hooks';
import { clearCurrentUser } from '_store/auth';
import { signOut } from 'aws-amplify/auth';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';

interface UserProfileProps {
  readOnly?: boolean;
}

const UserProfile: React.FC<UserProfileProps> = ({ readOnly = false }) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Get user data from Redux store
  const { currentUser, isLoading: reduxIsLoading } = useAppSelector(
    state => state.auth,
  );

  // Combine loading states
  const isLoading = reduxIsLoading && !currentUser;

  // Memoize the click handler
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleSignOut = useCallback(async () => {
    console.log('handleSignOut');
    setIsSigningOut(true);
    try {
      // Sign out from AWS Amplify first
      await signOut();

      // Clear Redux state
      await dispatch(clearCurrentUser());

      // Clear any stored authentication data
      localStorage.removeItem('currentUser');
      localStorage.removeItem('moi_auth_state');
      sessionStorage.clear();

      // Small delay to ensure all cleanup is complete, especially important for incognito mode
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if AWS Amplify sign-out fails, still clear local state
      await dispatch(clearCurrentUser());
      localStorage.removeItem('currentUser');
      localStorage.removeItem('moi_auth_state');
      sessionStorage.clear();

      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        navigate('/');
      }, 100);
    } finally {
      setIsOpen(false);
      setIsSigningOut(false);
    }
  }, [dispatch, navigate]);

  // Memoize user info calculation - use Redux data if available, fall back to auth data if needed
  const userInfo = useMemo(() => {
    // Default info if we have nothing
    const defaultInfo = {
      displayName: 'User',
      email: '',
      picture: '',
    };

    console.log('currentUser2', currentUser);

    // First try to use Redux data which has the most complete information
    if (currentUser) {
      return {
        displayName: currentUser.attributes.given_name || 'User',
        email: currentUser.attributes.email || '',
        picture: currentUser.attributes?.profile_photo || '',
      };
    }

    // If Redux data isn't available but we have auth context data, use that
    if (currentUser) {
      const email = currentUser.attributes?.email || currentUser.username || '';
      // Extract displayName from email if available
      const displayName = email.split('@')[0] || 'User';

      return {
        displayName,
        email,
        picture: '', // Auth data doesn't include picture
      };
    }

    // Fall back to default if neither is available
    return defaultInfo;
  }, [currentUser]);

  const { displayName, picture } = userInfo;

  return (
    <div className="flex items-center space-x-2">
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center gap-2">
          {!isLoading && (
            <div onClick={() => setIsOpen(!isOpen)}>
              <Avatar picture={picture} name={displayName} size="md" />
            </div>
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-2 text-gray-600 hover:text-gray-900 focus:outline-none`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <AiOutlineLoading3Quarters className="animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <span className="max-w-[120px] hidden md:block truncate">
                {displayName}
              </span>
            )}
          </button>
        </div>

        {/* Mobile view - always visible, but only if not readOnly */}
        {isOpen && (
          <div className="md:hidden absolute right-0 top-full mt-2 z-20 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
            {!isLoading && (
              <div className="py-1 flex flex-col gap-2" role="menu">
                <button
                  onClick={() => {
                    navigate('/my-account');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none hover:bg-blue-50"
                  role="menuitem"
                >
                  My Account
                </button>
                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className={`w-full text-left px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none hover:bg-blue-50 ${
                    isSigningOut ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  role="menuitem"
                >
                  {isSigningOut ? (
                    <div className="flex items-center gap-2">
                      <AiOutlineLoading3Quarters className="animate-spin" />
                      Signing Out...
                    </div>
                  ) : (
                    'Sign Out'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Desktop view - dropdown only if not readOnly */}
        {isOpen && (
          <div className="hidden md:block absolute right-0 top-full mt-2 z-20 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
            {!isLoading && (
              <div className="py-1" role="menu">
                <button
                  onClick={() => {
                    navigate('/my-account');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none hover:bg-blue-50"
                  role="menuitem"
                >
                  My Account
                </button>
                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none hover:bg-blue-50 ${
                    isSigningOut ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  role="menuitem"
                >
                  {isSigningOut ? (
                    <div className="flex items-center gap-2">
                      <AiOutlineLoading3Quarters className="animate-spin" />
                      Signing Out...
                    </div>
                  ) : (
                    'Sign Out'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
